import { useQuery } from "@tanstack/react-query";
import { db } from "@lyder-talk/core/lib/db-client";
import { queryKeys } from "@/lib/query-keys";
import { useAuth } from "./use-auth";
import type { VideoReviewItem, VideoPlatform } from "@/lib/video-types";

/** Number of days after which a video is considered due for review */
const REVIEW_THRESHOLD_DAYS = 1;

/**
 * Cap on how many practiced videos we pull. The reminder only ever shows the
 * top few, and we fetch oldest-practiced-first, so the earliest-due videos are
 * always included. Avoids scanning the user's entire practice history.
 */
const REVIEW_FETCH_CAP = 30;

/**
 * Hook to fetch videos that need review/practice.
 * A video needs review if:
 * - It has been practiced (has a last_practiced_at date)
 * - And the last practice was more than REVIEW_THRESHOLD_DAYS ago
 */
export function useVideoReviewItems() {
    const { userId } = useAuth();

    const query = useQuery({
        queryKey: queryKeys.videoReviewItems.byUser(userId),
        queryFn: async (): Promise<VideoReviewItem[]> => {
            if (!userId) return [];

            // Get all videos the user has practiced (with actual practice results)
            const { data: practiceData, error: practiceError } = await db.from('user_video_practice')
                .select(`
                    video_id,
                    last_practiced_at
                `)
                .eq('user_id', userId)
                .not('last_practiced_at', 'is', null)
                // Oldest practiced first = most due; cap so we don't scan the
                // user's entire practice history for a reminder that shows ~5.
                .order('last_practiced_at', { ascending: true })
                .limit(REVIEW_FETCH_CAP);

            if (practiceError) throw practiceError;
            if (!practiceData || practiceData.length === 0) return [];

            // Get unique video IDs
            const videoIds = [...new Set(practiceData.map(p => p.video_id))];

            // Fetch video details
            const { data: videosData, error: videosError } = await db.from('videos')
                .select('id, title, thumbnail_url, platform')
                .in('id', videoIds);

            if (videosError) throw videosError;
            if (!videosData) return [];

            // Create a map of video ID to latest practice date
            const lastPracticeMap = new Map<string, string | null>();
            for (const practice of practiceData) {
                const existing = lastPracticeMap.get(practice.video_id);
                const current = practice.last_practiced_at;

                if (!existing) {
                    lastPracticeMap.set(practice.video_id, current);
                } else if (current && existing) {
                    // Keep the most recent practice date
                    if (new Date(current) > new Date(existing)) {
                        lastPracticeMap.set(practice.video_id, current);
                    }
                }
            }

            // Calculate the threshold date
            const thresholdDate = new Date();
            thresholdDate.setDate(thresholdDate.getDate() - REVIEW_THRESHOLD_DAYS);

            // Filter videos that need review
            const videosDue: VideoReviewItem[] = [];

            for (const video of videosData) {
                const lastPracticedAt = lastPracticeMap.get(video.id);

                // Check if video needs review
                const needsReview = !lastPracticedAt ||
                    new Date(lastPracticedAt) < thresholdDate;

                if (needsReview) {
                    videosDue.push({
                        videoId: video.id,
                        title: video.title,
                        thumbnailUrl: video.thumbnail_url || undefined,
                        lastPracticedAt: lastPracticedAt || null,
                        platform: video.platform as VideoPlatform,
                    });
                }
            }

            // Sort by last practiced date (oldest first, null values first)
            videosDue.sort((a, b) => {
                if (!a.lastPracticedAt && !b.lastPracticedAt) return 0;
                if (!a.lastPracticedAt) return -1;
                if (!b.lastPracticedAt) return 1;
                return new Date(a.lastPracticedAt).getTime() - new Date(b.lastPracticedAt).getTime();
            });

            return videosDue;
        },
        enabled: !!userId,
        staleTime: 1000 * 60 * 5, // 5 minutes
        refetchOnWindowFocus: false,
    });

    return {
        videosDue: query.data ?? [],
        isLoading: query.isLoading,
        error: query.error,
        refetch: query.refetch,
    };
}
